/**
 * Composable pour simplifier l'utilisation du store de cache des assets
 * Fournit des méthodes réactives et des helpers pour chaque type d'asset
 */

import type { AgentWithMemos } from '@/api/types/entities/agent/relations';
import type { DigitalEntity } from '@/api/types/entities/digitalEntity/types';
import type { Location } from '@/api/types/entities/location/types';
import type { MaterialEntity } from '@/api/types/entities/materialEntity/types';
import type { Project } from '@/api/types/entities/project/types';
import type { Memo } from '@/api/types/memos/types';
import { useAssetsCacheStore } from '@/stores/assetsCache';
import { useAuthStore } from '@/stores/auth';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

type AssetType = 'agents' | 'locations' | 'digitalEntities' | 'materialEntities' | 'projects' | 'memos';
type FilterMode = 'my' | 'accessible' | 'public';

interface UseAssetsOptions {
  assetType: AssetType;
  autoLoad?: boolean;
  defaultFilter?: FilterMode;
}

interface UseAssetsReturn<T> {
  // État
  allAssets: Ref<T[]>;
  filteredAssets: ComputedRef<T[]>;
  isLoading: ComputedRef<boolean>;
  stats: ComputedRef<{ total: number; my: number; public: number }>;
  
  // Filtres
  viewFilter: Ref<FilterMode>;
  setFilter: (filter: FilterMode) => void;
  
  // Actions
  loadAssets: (forceRefresh?: boolean) => Promise<void>;
  refreshAssets: () => Promise<void>;
  addAsset: (asset: T) => void;
  updateAsset: (asset: T) => void;
  removeAsset: (assetUuid: string) => void;
  
  // Helpers
  isMyAsset: (asset: T) => boolean;
  isPublicAsset: (asset: T) => boolean;
  canEditAsset: (asset: T) => boolean;
  canDeleteAsset: (asset: T) => boolean;
}

export function useAssets<T extends { uuid: string; user_id: string; is_public: boolean }>(
  options: UseAssetsOptions
): UseAssetsReturn<T> {
  const { assetType, autoLoad = true, defaultFilter = 'my' } = options;
  
  // Stores
  const assetsCacheStore = useAssetsCacheStore();
  const authStore = useAuthStore();
  
  // État local
  const allAssets = ref<T[]>([]);
  const viewFilter = ref<FilterMode>(defaultFilter);
  
  // Computed
  const userId = computed((): string => authStore.user?.uuid || '');
  const isLoading = computed((): boolean => assetsCacheStore.isLoadingAssets);
  
  // Statistiques selon le type d'asset
  const stats = computed(() => {
    switch (assetType) {
      case 'agents': return assetsCacheStore.agentsStats;
      case 'locations': return assetsCacheStore.locationsStats;
      case 'digitalEntities': return assetsCacheStore.digitalEntitiesStats;
      case 'materialEntities': return assetsCacheStore.materialEntitiesStats;
      case 'projects': return assetsCacheStore.projectsStats;
      case 'memos': return assetsCacheStore.memosStats;
      default: return { total: 0, my: 0, public: 0 };
    }
  });
  
  // Assets filtrés selon le mode sélectionné
  const filteredAssets = computed((): T[] => {
    switch (viewFilter.value) {
      case 'my':
        return allAssets.value.filter(asset => asset.user_id === userId.value) as T[];
      case 'public':
        return allAssets.value.filter(asset => asset.is_public && asset.user_id !== userId.value) as T[];
      case 'accessible':
      default:
        return allAssets.value as T[];
    }
  });
  
  // Helpers de permissions
  const isMyAsset = (asset: T): boolean => {
    return asset.user_id === userId.value;
  };
  
  const isPublicAsset = (asset: T): boolean => {
    return asset.is_public && asset.user_id !== userId.value;
  };
  
  const canEditAsset = (asset: T): boolean => {
    return authStore.isAdmin || asset.user_id === userId.value;
  };
  
  const canDeleteAsset = (asset: T): boolean => {
    return authStore.isAdmin || asset.user_id === userId.value;
  };
  
  // Actions
  const loadAssets = async (forceRefresh: boolean = false): Promise<void> => {
    try {
      //console.log(`[useAssets] loadAssets appelé pour ${assetType}, forceRefresh:`, forceRefresh);
      const assets = await assetsCacheStore.getAssets<T>(assetType, forceRefresh);
      //console.log(`[useAssets] Assets récupérés du cache:`, assets.length, 'éléments');
      allAssets.value = assets;
    } catch (error) {
      console.error(`Erreur lors du chargement des ${assetType}:`, error);
      throw error;
    }
  };
  
  const refreshAssets = async (): Promise<void> => {
    await loadAssets(true);
  };
  
  const addAsset = (asset: T): void => {
    //console.log(`[useAssets] addAsset appelé pour ${assetType}:`, asset);
    
    // Vérifier si l'asset existe déjà dans allAssets
    const existsInLocal = allAssets.value.some(a => a.uuid === asset.uuid);
    if (existsInLocal) {
      console.log(`[useAssets] Asset déjà présent dans allAssets, pas d'ajout local`);
    } else {
      console.log(`[useAssets] Ajout de l'asset à allAssets`);
      (allAssets.value as T[]).unshift(asset);
    }
    
    // Ajouter au cache (AssetEntity non exporté, utilisation d'any nécessaire)
    assetsCacheStore.addAssetToCache(assetType, asset as any);
  };
  
  const updateAsset = (asset: T): void => {
    // Mettre à jour le cache (AssetEntity non exporté, utilisation d'any nécessaire)
    assetsCacheStore.updateAssetInCache(assetType, asset as any);
    const index = allAssets.value.findIndex(a => a.uuid === asset.uuid);
    if (index !== -1) {
      (allAssets.value as T[])[index] = asset;
    }
  };
  
  const removeAsset = (assetUuid: string): void => {
    assetsCacheStore.removeAssetFromCache(assetType, assetUuid);
    allAssets.value = allAssets.value.filter(a => a.uuid !== assetUuid);
  };
  
  const setFilter = (filter: FilterMode): void => {
    viewFilter.value = filter;
  };
  
  // Auto-chargement si demandé
  if (autoLoad) {
    loadAssets().catch(console.error);
  }
  
  return {
    // État
    allAssets: allAssets as Ref<T[]>,
    filteredAssets,
    isLoading,
    stats,
    
    // Filtres
    viewFilter,
    setFilter,
    
    // Actions
    loadAssets,
    refreshAssets,
    addAsset,
    updateAsset,
    removeAsset,
    
    // Helpers
    isMyAsset,
    isPublicAsset,
    canEditAsset,
    canDeleteAsset
  };
}

// Composables spécialisés pour chaque type d'asset
export const useAgents = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<AgentWithMemos> => 
  useAssets<AgentWithMemos>({ assetType: 'agents', ...options });

export const useAgentsWithMemos = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<AgentWithMemos> => 
  useAssets<AgentWithMemos>({ assetType: 'agents', ...options });

export const useLocations = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<Location> => 
  useAssets<Location>({ assetType: 'locations', ...options });

export const useDigitalEntities = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<DigitalEntity> => 
  useAssets<DigitalEntity>({ assetType: 'digitalEntities', ...options });

export const useMaterialEntities = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<MaterialEntity> => 
  useAssets<MaterialEntity>({ assetType: 'materialEntities', ...options });

export const useProjects = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<Project> => 
  useAssets<Project>({ assetType: 'projects', ...options });

export const useMemos = (options?: Partial<UseAssetsOptions>): UseAssetsReturn<Memo> => 
  useAssets<Memo>({ assetType: 'memos', ...options }); 